// Copyright 2026 L3montree GmbH and the DevGuard Contributors.
// SPDX-License-Identifier: 	AGPL-3.0-or-later

"use client";

import { TourCard } from "@/components/common/tours/TourCard";
import { TourProvider, useTour } from "@reactour/tour";
import type { StepType } from "@reactour/tour";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import type { TourContextType } from "@/types/view/tour";

const TourContext = React.createContext<TourContextType>({
  registerSteps: () => {},
  openTour: () => {},
  hasSteps: false,
});

function TourController({
  steps,
  shouldOpen,
  onOpened,
  children,
}: {
  steps: StepType[];
  shouldOpen: boolean;
  onOpened: () => void;
  children: React.ReactNode;
}) {
  const { setSteps, setIsOpen, setCurrentStep, isOpen } = useTour();

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (steps.length > 0) {
      setSteps?.(steps);
    }
    // setSteps is stable, safe to omit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps]);

  useEffect(() => {
    if (shouldOpen && steps.length > 0) {
      setCurrentStep?.(0);
      setIsOpen?.(true);
      onOpened();
    }
    // setIsOpen/setCurrentStep are stable, safe to omit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldOpen, steps, onOpened]);

  return <>{children}</>;
}

export function TourContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [steps, setSteps] = useState<StepType[]>([]);
  const [shouldOpen, setShouldOpen] = useState(false);

  const registerSteps = useCallback((newSteps: StepType[]) => {
    setSteps(newSteps);
  }, []);

  const openTour = useCallback(() => {
    setShouldOpen(true);
  }, []);

  return (
    <TourContext.Provider
      value={useMemo(
        () => ({ registerSteps, openTour, hasSteps: steps.length > 0 }),
        [registerSteps, openTour, steps.length],
      )}
    >
      <TourProvider
        steps={steps}
        ContentComponent={TourCard}
        styles={{
          popover: (base) => ({
            ...base,
            padding: 0,
            background: "transparent",
            boxShadow: "none",
          }),
        }}
      >
        <TourController
          steps={steps}
          shouldOpen={shouldOpen}
          onOpened={() => setShouldOpen(false)}
        >
          {children}
        </TourController>
      </TourProvider>
    </TourContext.Provider>
  );
}

export const useTourContext = () => React.useContext(TourContext);
